import { Environment } from '@robinpowered/dashboard-apps-common';

const {
  NODE_ENV,
  REACT_APP_ROBIN_ENV,
  REACT_APP_DASHBOARD_URL,
  REACT_APP_DASHBOARD_API_URL,
  REACT_APP_ROBIN_API_URL,
  REACT_APP_ROBIN_BASE_API_URL,
  REACT_APP_INTEGRATIONS_SERVICE_URL,
  REACT_APP_IMAGE_SERVER_URL,
  REACT_APP_ROBIN_INTERCOM_APP_ID,
  REACT_APP_SENTRY_DSN_SHELL,
  REACT_APP_FEATURE_FLAG_URL,
  REACT_APP_COOKIE_NAME,
  REACT_APP_GQL_URL,
  REACT_APP_NAME_SHELL,
  REACT_APP_VERSION_SHELL,
  REACT_APP_AMPLITUDE_API_KEY_SHELL,
  REACT_APP_GLOBAL_AGENT_URL,
  REACT_APP_GOOGLE_CLIENT_ID,
  REACT_APP_MICROSOFT_CLIENT_ID,
} = process.env;

type Config = {
  env: Environment;
  robinEnv: Environment;
  dashboardUrl: string;
  dashboardApiUrl: string;
  /** Versioned Robin REST API base (e.g. `https://api.robindev.co/v1.0`). Used by the shell's tenant-less event probe and threaded through to `useEventComposerConfig`. */
  apiUrl: string;
  /** Unversioned Robin REST API base (e.g. `https://api.robindev.co`). Required by `EventComposer`/`EventDetails` patterns. */
  baseApiUrl: string;
  integrationsServiceUrl: string;
  imageServerUrl: string;
  robinIntercomAppId: string;
  sentryDsn: string;
  featureFlagUrl: string;
  sessionCookieName: string;
  gqlUrl: string;
  appName: string;
  appVersion: string;
  amplitudeApiKey: string;
  agentChatUrl?: string;
  /**
   * OAuth client IDs for reconnecting a user's calendar account in place. Both
   * are optional and stay out of the required-env check below so the shell
   * still boots in environments that have not configured them; the reconnect
   * flow reports a failure instead of opening a doomed popup.
   */
  googleClientId?: string;
  microsoftClientId?: string;
};

[
  'NODE_ENV',
  'REACT_APP_ROBIN_ENV',
  'REACT_APP_DASHBOARD_API_URL',
  'REACT_APP_ROBIN_API_URL',
  'REACT_APP_ROBIN_BASE_API_URL',
  'REACT_APP_INTEGRATIONS_SERVICE_URL',
  'REACT_APP_IMAGE_SERVER_URL',
  'REACT_APP_ROBIN_INTERCOM_APP_ID',
  'REACT_APP_SENTRY_DSN_SHELL',
  'REACT_APP_FEATURE_FLAG_URL',
  'REACT_APP_COOKIE_NAME',
  'REACT_APP_GQL_URL',
  'REACT_APP_NAME_SHELL',
  'REACT_APP_VERSION_SHELL',
  'REACT_APP_AMPLITUDE_API_KEY_SHELL',
].forEach((key) => {
  if (process.env.NODE_ENV !== 'test' && process.env[key] === undefined) {
    throw new Error(`Missing ${key} on '.`);
  }
});

export const config: Config = {
  env: NODE_ENV as Environment,
  dashboardUrl: REACT_APP_DASHBOARD_URL as string,
  dashboardApiUrl: REACT_APP_DASHBOARD_API_URL as string,
  apiUrl: REACT_APP_ROBIN_API_URL as string,
  baseApiUrl: REACT_APP_ROBIN_BASE_API_URL as string,
  integrationsServiceUrl: REACT_APP_INTEGRATIONS_SERVICE_URL as string,
  imageServerUrl: REACT_APP_IMAGE_SERVER_URL as string,
  robinIntercomAppId: REACT_APP_ROBIN_INTERCOM_APP_ID as string,
  robinEnv: REACT_APP_ROBIN_ENV as Environment,
  sentryDsn: REACT_APP_SENTRY_DSN_SHELL as string,
  featureFlagUrl: REACT_APP_FEATURE_FLAG_URL as string,
  sessionCookieName: REACT_APP_COOKIE_NAME as string,
  gqlUrl: REACT_APP_GQL_URL as string,
  appName: REACT_APP_NAME_SHELL as string,
  appVersion: REACT_APP_VERSION_SHELL as string,
  amplitudeApiKey: REACT_APP_AMPLITUDE_API_KEY_SHELL as string,
  // Trailing slash trimmed so `${agentChatUrl}/agent-chat-app.js` can't
  // produce a double slash.
  agentChatUrl: REACT_APP_GLOBAL_AGENT_URL?.replace(/\/+$/, ''),
  googleClientId: REACT_APP_GOOGLE_CLIENT_ID,
  microsoftClientId: REACT_APP_MICROSOFT_CLIENT_ID,
};

/* eslint-disable no-console */
if (config.env === 'development') {
  console.groupCollapsed('Started app with config');
  console.table(config);
  console.groupEnd();
}
/* eslint-enable no-console */
